/**
 * WebRTC Call Quality Monitor - Signal-grade real-time stats
 * Polls RTCPeerConnection.getStats() every 2s and derives a quality score.
 * Includes adaptive bitrate control based on network conditions.
 */

export type CallQuality = 'excellent' | 'good' | 'poor' | 'critical';

export interface CallQualityStats {
  quality: CallQuality;
  packetLossRatio: number;
  jitter: number;         // ms
  roundTripTime: number;  // ms
  audioBitrate: number;   // bps
  videoBitrate: number;   // bps
  timestamp: number;
}

const POLL_INTERVAL_MS = 2000;

// Thresholds (Signal-style)
const THRESHOLDS = {
  excellent: { packetLoss: 0.01, rtt: 150, jitter: 30 },
  good:      { packetLoss: 0.03, rtt: 300, jitter: 80 },
  poor:      { packetLoss: 0.05, rtt: 500, jitter: 150 },
  // above poor = critical
};

// Adaptive bitrate tiers
const VIDEO_BITRATE_TIERS = [
  { maxBitrate: 1_500_000, label: '720p' },  // excellent
  { maxBitrate: 600_000,   label: '360p' },   // good
  { maxBitrate: 150_000,   label: '144p' },   // poor
  // critical = audio only (0)
];

function deriveQuality(packetLoss: number, rtt: number, jitter: number): CallQuality {
  if (packetLoss <= THRESHOLDS.excellent.packetLoss && rtt <= THRESHOLDS.excellent.rtt && jitter <= THRESHOLDS.excellent.jitter) {
    return 'excellent';
  }
  if (packetLoss <= THRESHOLDS.good.packetLoss && rtt <= THRESHOLDS.good.rtt && jitter <= THRESHOLDS.good.jitter) {
    return 'good';
  }
  if (packetLoss <= THRESHOLDS.poor.packetLoss && rtt <= THRESHOLDS.poor.rtt && jitter <= THRESHOLDS.poor.jitter) {
    return 'poor';
  }
  return 'critical';
}

export class CallQualityMonitor {
  private pc: RTCPeerConnection;
  private timer: ReturnType<typeof setInterval> | null = null;
  private onUpdate: (stats: CallQualityStats) => void;
  private prevBytesReceived: { audio: number; video: number } = { audio: 0, video: 0 };
  private prevTimestamp = 0;
  private poorSince: number | null = null;
  private onPoorConnection: (() => void) | null;
  private adaptiveBitrate: boolean;
  private lastAdaptedQuality: CallQuality | null = null;

  constructor(
    pc: RTCPeerConnection,
    onUpdate: (stats: CallQualityStats) => void,
    options?: {
      onPoorConnection?: () => void;
      adaptiveBitrate?: boolean;
    }
  ) {
    this.pc = pc;
    this.onUpdate = onUpdate;
    this.onPoorConnection = options?.onPoorConnection ?? null;
    this.adaptiveBitrate = options?.adaptiveBitrate ?? true;
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
    this.poll(); // immediate first poll
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private async poll() {
    if (this.pc.connectionState === 'closed') {
      this.stop();
      return;
    }

    try {
      const stats = await this.pc.getStats();
      let packetLoss = 0;
      let jitter = 0;
      let rtt = 0;
      let audioBytes = 0;
      let videoBytes = 0;
      let totalPackets = 0;
      let lostPackets = 0;

      stats.forEach((report) => {
        if (report.type === 'inbound-rtp') {
          totalPackets += report.packetsReceived || 0;
          lostPackets += report.packetsLost || 0;
          jitter = Math.max(jitter, (report.jitter || 0) * 1000);
          if (report.kind === 'audio') audioBytes = report.bytesReceived || 0;
          if (report.kind === 'video') videoBytes = report.bytesReceived || 0;
        }
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          rtt = report.currentRoundTripTime ? report.currentRoundTripTime * 1000 : rtt;
        }
      });

      packetLoss = totalPackets > 0 ? lostPackets / (totalPackets + lostPackets) : 0;

      // Calculate bitrates
      const now = Date.now();
      const elapsed = this.prevTimestamp > 0 ? (now - this.prevTimestamp) / 1000 : 2;
      const audioBitrate = elapsed > 0 ? ((audioBytes - this.prevBytesReceived.audio) * 8) / elapsed : 0;
      const videoBitrate = elapsed > 0 ? ((videoBytes - this.prevBytesReceived.video) * 8) / elapsed : 0;
      this.prevBytesReceived = { audio: audioBytes, video: videoBytes };
      this.prevTimestamp = now;

      const quality = deriveQuality(packetLoss, rtt, jitter);

      const result: CallQualityStats = {
        quality,
        packetLossRatio: packetLoss,
        jitter,
        roundTripTime: rtt,
        audioBitrate: Math.max(0, audioBitrate),
        videoBitrate: Math.max(0, videoBitrate),
        timestamp: now,
      };

      this.onUpdate(result);

      // Poor connection notification (sustained > 5s)
      if (quality === 'poor' || quality === 'critical') {
        if (!this.poorSince) this.poorSince = now;
        if (now - this.poorSince > 5000 && this.onPoorConnection) {
          this.onPoorConnection();
          this.poorSince = now + 30000; // don't re-trigger for 30s
        }
      } else {
        this.poorSince = null;
      }

      // Adaptive bitrate control
      if (this.adaptiveBitrate && quality !== this.lastAdaptedQuality) {
        this.lastAdaptedQuality = quality;
        this.adaptVideoQuality(quality);
      }
    } catch (err) {
      console.warn('[CallQuality] Stats poll failed:', err);
    }
  }

  private adaptVideoQuality(quality: CallQuality) {
    try {
      const senders = this.pc.getSenders();
      const videoSender = senders.find(s => s.track?.kind === 'video');
      if (!videoSender) return;

      const params = videoSender.getParameters();
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{}];
      }

      let tierIndex: number;
      switch (quality) {
        case 'excellent': tierIndex = 0; break;
        case 'good': tierIndex = 1; break;
        case 'poor': tierIndex = 2; break;
        case 'critical':
          // Audio-only fallback - disable video track temporarily
          if (videoSender.track) videoSender.track.enabled = false;
          console.log('[CallQuality] Critical - disabled video');
          return;
        default: tierIndex = 0;
      }

      // Re-enable video if was disabled
      if (videoSender.track && !videoSender.track.enabled) {
        videoSender.track.enabled = true;
      }

      const tier = VIDEO_BITRATE_TIERS[tierIndex];
      params.encodings[0].maxBitrate = tier.maxBitrate;

      videoSender.setParameters(params).then(() => {
        console.log(`[CallQuality] Adapted video to ${tier.label} (${quality})`);
      }).catch(() => {});
    } catch (err) {
      console.warn('[CallQuality] Adaptive bitrate failed:', err);
    }
  }
}
